/**
 * `useSeenBatch` — the shared "which notifications has this surface actually shown?"
 * bookkeeping (desktop slice 4, R4-d-2).
 *
 * WHY THIS EXISTS. The extension side panel and the web page each carried their own
 * copy of this block. Not similar copies — after normalising a single local variable
 * name (`panelVisible` vs `pageVisible`) the two 16-line regions were **byte-identical**.
 * A third surface (desktop, Slice 6) is about to need the same thing, and the failure
 * mode a copy invites is silent: a host that forgets the seen-id set double-counts
 * `notifications_seen_count`, and a host that forgets to route through `trackSeenBatch`
 * reports First Light as organic traffic, quietly corrupting the launch-gate ratio.
 *
 * WHAT IT DOES NOT DO — and this is the load-bearing part. It does **not** call
 * `splitSeenBatch`. "Seen" here means *rendered while this surface was visible*, which
 * is why the visibility gate is inside the hook rather than left to callers. Folding
 * the First Light split into the intake pipeline instead was considered and rejected
 * (S11/R4-d): it would silently redefine `first_notification_seen` from "first seen"
 * to "first delivered" — a metric regression wearing a refactor's clothes. The split
 * stays in the tracker the host injects.
 *
 * NOT IN `client-core`, despite R4-d-2 saying the helper should live "beside
 * `trackSeenBatch`". Following that literally breaks the CLI build, and the mechanism is
 * worth stating precisely because the obvious version of the argument is wrong:
 *
 * It is NOT that a `react` import in `client-core` fails to compile. It compiles fine —
 * `@types/react` resolves through the hoisted root `node_modules` even though
 * `client-core` never declares it, and `apps/cli` still typechecks. Nothing enforces the
 * absence: there is no boundary test banning React from `client-core` the way
 * `apiSubpathBoundary.test.ts` guards `./api`. Today it is convention, not a rule.
 *
 * What actually breaks is the emit. "Beside `trackSeenBatch`" means
 * `client-core/src/analytics/`, which `src/index.ts` re-exports from the root barrel;
 * `client-core` builds CommonJS, so `export *` is not tree-shakeable and the re-export
 * becomes an unconditional `require("react")` in `dist/index.js`. `apps/cli` imports that
 * barrel and compiles with `bun build --compile`, so React would be pulled into the
 * binary — and R1-b forbids that cone from growing native/runtime weight.
 *
 * `client-ui` is "Headless React" in the spec's own package table, already declares
 * react/react-dom as peers, and already hosts hooks. The hook lives here; the pure
 * tracker stays where it is.
 *
 * AND YES, THIS PACKAGE'S `CLAUDE.md` LISTS "dedup" UNDER WHAT DOES NOT BELONG HERE.
 * That rule is about transport-level dedup — the SSE/storage pipeline that decides a
 * *message* was already received, which is `client-core`'s job. This is view-level: has
 * this surface already *shown* the user something. The precedent is `useAutoMarkRead`,
 * which lives two directories away, owns two dedup sets of its own (its comments call
 * one "cheap dedup"), observes rendering, and surfaces the write through an injected
 * callback rather than touching storage. Same shape, same tier, same reason.
 */

/*
 * R4-d-4 — WHO CALLS THIS, answered by name because the spec makes it a checklist item
 * rather than an inference:
 *
 *   - `apps/extension`  SidePanelNotificationList.tsx   ✅ calls it
 *   - `apps/web`        MainPage.tsx                     ✅ calls it
 *   - `apps/desktop`    in-app notification list         ⏳ Slice 6 — this is the wiring
 *                                                           that makes AC-12a non-vacuous
 *   - `apps/cli`        —                                ❌ never. `reflecto tail` streams;
 *                                                           it renders no list, so it has
 *                                                           no notion of "seen".
 *
 * R4-d-2 also asks for "a plain function for the CLI", which reads at first like it
 * contradicts R4-d-4 above. It does not: the plain function is `trackSeenBatch`, already
 * shipped in `client-core` and callable without React by anything that ever needs it. So
 * R4-d-2 describes the existing split (plain core + React wrapper) rather than commissioning
 * new CLI code — and building a CLI variant would ship a seam export with zero callers,
 * against a checklist that names its callers exhaustively.
 */

import { useEffect, useRef, useState } from "react";

/** The only two fields this hook needs — satisfied structurally by `DecryptedNotification`. */
export interface SeenBatchItem {
  msgId: string;
  appPackage: string;
}

/**
 * Report each notification exactly once, the first time it is rendered while the
 * surface is visible.
 *
 * @param notifications the list currently rendered; `undefined` before the first load.
 * @param track host-bound reporter — in practice each host's `trackSeenBatch` wrapper,
 *   already bound to that host's storage adapter and analytics client. Called with only
 *   the newly-seen items, never the whole list.
 */
export function useSeenBatch<T extends SeenBatchItem>(
  notifications: readonly T[] | undefined,
  track: (items: T[]) => void | Promise<void>
): void {
  // Session-scoped: a reload legitimately re-counts, because the metric is
  // "notifications this surface showed you", not "notifications that exist".
  //
  // DELIBERATELY UNBOUNDED, and do not "align" it with the capped tracker in
  // `@reflecto/client-core/intake` — the two sets look alike and guard opposite things.
  // There, eviction is safe: a re-delivered msgId that falls out of the window is merely
  // displayed twice in a stream. Here it would be *reported* twice, bumping
  // `notifications_seen_count` for a notification the user saw once — so a cap would
  // introduce the exact miscount this set exists to prevent, and would do it silently,
  // only on the long sessions nobody tests. Growth is one short string per notification
  // shown, discarded on reload.
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Held in a ref so a host passing an inline lambda neither re-runs the effect on
  // every render nor gets stale-closed over an old tracker. Both current hosts pass a
  // stable module-level function, so this costs nothing today and removes a trap for
  // the desktop wiring in Slice 6.
  const trackRef = useRef(track);
  trackRef.current = track;

  // Kept in the non-lazy form both hosts used. A lazy initializer would be marginally
  // cheaper and provably identical, but this slice is a move: an improvement folded into
  // one is indistinguishable from a regression until someone bisects it.
  const [visible, setVisible] = useState(document.visibilityState === "visible");

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    // `visible` is a dependency, not just a guard: a batch that arrives while the
    // surface is hidden must count when the user comes back, not be lost.
    if (!notifications || !visible) return;
    const fresh = notifications.filter((n) => !seenIdsRef.current.has(n.msgId));
    if (fresh.length === 0) return;
    for (const n of fresh) seenIdsRef.current.add(n.msgId);
    void trackRef.current(fresh);
  }, [notifications, visible]);
}
